import React, { useState, useEffect } from "react";
import KanbanColumn from "./KanbanColumn";
import toast from "react-hot-toast";
import API from "../../../../../services/api";
import ItemModal from "../components/itemModal";
import { useLocation, useNavigate } from "react-router-dom";
import RoadmapColumn from "./roadmapColumn";
import { BiPlus } from "react-icons/bi";
import CreateTicket from "./createTicket";
import BacklogFilters from "./BacklogFilters";
import FeaturePedagogyModal from "./FeaturePedagogyModal";
import { motion, AnimatePresence } from "framer-motion";

const columns = [
  {
    id: "TODO",
    title: "To Do",
    color: "bg-gray-500",
    borderColor: "border-gray-500",
  },
  {
    id: "READY_TO_DEV",
    title: "Ready to Dev",
    color: "bg-pink-500",
    borderColor: "border-pink-500",
  },
  {
    id: "IN_PROGRESS",
    title: "In Progress",
    color: "bg-blue-600",
    borderColor: "border-blue-600",
  },
  {
    id: "TESTING",
    title: "Testing",
    color: "bg-purple-500",
    borderColor: "border-purple-500",
  },
  {
    id: "DONE",
    title: "Done",
    color: "bg-green-500",
    borderColor: "border-green-500",
  },
  {
    id: "ARCHIVED",
    title: "Archived",
    color: "bg-gray-500",
    borderColor: "border-gray-500",
  },
];

const Backlog = ({ project, showRoadmap, setShowRoadmap }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);

  // New state for pedagogy modal
  const [isPedagogyModalOpen, setIsPedagogyModalOpen] = useState(false);
  const [pendingTicket, setPendingTicket] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    categories: [],
    module_ids: [],
    users: [],
  });

  // States for drag and drop
  const [draggedBacklogItem, setDraggedBacklogItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);

  // Auto-scroll state
  const [autoScrolling, setAutoScrolling] = useState(null);

  //Roadmap Column part
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Add refs for roadmap callbacks
  const [callbacks, setCallbacks] = useState({
    onItemUpdate: null,
    onItemDelete: null,
  });

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const itemModalId = query.get("item_modal_id");
  const navigate = useNavigate();

  // Update items when refreshTrigger changes (for full refreshes)
  useEffect(() => {
    fetch();
  }, [filters]);

  // Around line 92-97, in the useEffect for autoScrolling
  useEffect(() => {
    // Add global dragend listener to clear auto-scrolling
    const handleGlobalDragEnd = () => {
      if (autoScrolling) {
        clearInterval(autoScrolling);
        setAutoScrolling(null);
      }
    };

    document.addEventListener("dragend", handleGlobalDragEnd);

    return () => {
      // Clear auto-scroll interval on unmount
      if (autoScrolling) {
        clearInterval(autoScrolling);
      }
      document.removeEventListener("dragend", handleGlobalDragEnd);
    };
  }, [autoScrolling]);

  async function fetch() {
    setLoading(true);
    try {
      const { data, ok } = await API.post("/roadlog_item/search", {
        project_id: project._id,
        is_in_backlog: "yes",
        ...filters,
      });
      if (!ok) throw new Error("Error fetching roadlog items");
      setItems(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching roadlog items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  // Déplacer un item entre colonnes ou dans la même colonne
  const moveItem = async (itemId, sourceColId, targetColId, targetIndex = null) => {
    try {
      // Trouver l'item à déplacer
      const itemToMove = items.find((item) => item._id === itemId);
      if (!itemToMove) return;

      // --- PRÉPARATION DES DONNÉES ---

      // Créer une copie de tous les items (sans l'item déplacé)
      let newItems = items.filter((item) => item._id !== itemId);

      // Mise à jour du statut de l'item déplacé
      const updatedItem = { ...itemToMove, status: targetColId };

      // --- CALCUL DES NOUVELLES POSITIONS ---

      // Fonction pour repositionner les items par ordre séquentiel
      const reindexItems = (itemsList) =>
        itemsList.map((item, index) => ({
          ...item,
          position: index,
        }));

      // Récupérer et trier les items des colonnes concernées
      const targetItems = newItems.filter((item) => item.status === targetColId).sort((a, b) => a.position - b.position);

      // Index où insérer l'item (fin par défaut)
      const insertAt = targetIndex !== null ? Math.min(targetIndex, targetItems.length) : targetItems.length;

      // Insérer l'item à la bonne position
      targetItems.splice(insertAt, 0, updatedItem);

      // Recalculer les positions des items dans la colonne cible
      const updatedTargetItems = reindexItems(targetItems);

      // Liste des items à mettre à jour sur le serveur
      let itemsToUpdate = [
        {
          id: itemId,
          status: targetColId,
          position: updatedItem.position,
        },
      ];

      // Ajouter les autres items de la colonne cible dont la position a changé
      itemsToUpdate = [...itemsToUpdate, ...updatedTargetItems.filter((item) => item._id !== itemId).map((item) => ({ id: item._id, position: item.position }))];

      // Si l'item change de colonne, réindexer aussi la colonne source
      let updatedSourceItems = [];
      if (sourceColId !== targetColId) {
        const sourceItems = newItems.filter((item) => item.status === sourceColId).sort((a, b) => a.position - b.position);

        updatedSourceItems = reindexItems(sourceItems);

        // Ajouter les items de la colonne source à mettre à jour
        itemsToUpdate = [...itemsToUpdate, ...updatedSourceItems.map((item) => ({ id: item._id, position: item.position }))];
      }

      // --- MISE À JOUR DE L'INTERFACE UTILISATEUR ---

      // Reconstruire la liste complète d'items mise à jour
      const finalItems = [
        ...newItems.filter((item) => item.status !== targetColId && (sourceColId === targetColId || item.status !== sourceColId)),
        ...updatedTargetItems,
        ...(sourceColId !== targetColId ? updatedSourceItems : []),
      ];

      // Mise à jour de l'UI
      setItems(finalItems);

      // --- MISE À JOUR DU SERVEUR ---

      // Mise à jour de l'item principal
      const mainUpdate = await API.put(`/roadlog_item/${itemId}`, {
        status: targetColId,
        position: insertAt,
      });

      if (!mainUpdate.ok) throw new Error("Erreur lors de la mise à jour de l'item principal");

      // Mise à jour des positions des autres items affectés (en parallèle)
      const otherUpdates = itemsToUpdate.filter((item) => item.id !== itemId).map((item) => API.put(`/roadlog_item/${item.id}`, { position: item.position }));

      if (otherUpdates.length > 0) {
        const results = await Promise.all(otherUpdates);
        if (results.some((result) => !result.ok)) {
          throw new Error("Certaines mises à jour ont échoué");
        }
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Erreur lors de la mise à jour");
      fetch(); // Recharger les données en cas d'erreur
    }
  };

  // Handlers pour le drag and drop
  const handleDragStart = (e, itemId, columnId) => {
    e.dataTransfer.setData("itemId", itemId);
    e.dataTransfer.setData("sourceColumnId", columnId);
    e.dataTransfer.setData("type", "item");

    const item = items.find((item) => item._id === itemId);
    setDraggedBacklogItem({ item, columnId });
  };

  const handleDrop = async (e, columnId, dropIndex = null) => {
    e.preventDefault();
    e.stopPropagation();
    const itemType = e.dataTransfer.getData("type");

    // Reset drag states
    setDraggedBacklogItem(null);
    setDragOverColumn(null);
    setDragOverItemIndex(null);

    // Clear scrolling interval (horizontal scroll in main container)
    if (autoScrolling) {
      clearInterval(autoScrolling);
      setAutoScrolling(null);
    }

    // Dispatch global event to clear all column auto-scrolling
    document.dispatchEvent(new CustomEvent("clearColumnAutoScroll"));

    // Handle roadlog_item drops
    if (itemType === "roadlog_item") {
      const itemData = e.dataTransfer.getData("item");
      const item = JSON.parse(itemData);

      // Calculate position for insertion
      const actualDropIndex = dragOverItemIndex !== null ? dragOverItemIndex : dropIndex !== null ? dropIndex : null;

      // Add the item to the column with updated status and position
      setItems((prevItems) => [
        ...prevItems,
        {
          ...item,
          status: columnId,
          position: actualDropIndex,
          is_in_backlog: true,
        },
      ]);

      try {
        const { ok } = await API.put(`/roadlog_item/${item._id}`, {
          status: columnId,
          position: actualDropIndex,
          is_in_backlog: true,
        });

        if (!ok) {
          setItems((prevItems) => prevItems.filter((i) => i._id !== item._id));
          throw new Error("Failed to update item");
        }
      } catch (error) {
        console.error(error);
        setItems((prevItems) => prevItems.filter((i) => i._id !== item._id));
        toast.error("Error updating item");
      }

      return;
    }

    // Handling regular items
    const itemId = e.dataTransfer.getData("itemId");
    const sourceColumnId = e.dataTransfer.getData("sourceColumnId");

    // Déterminer l'index de drop à partir de dragOverItemIndex ou dropIndex
    const actualDropIndex = dragOverItemIndex !== null ? dragOverItemIndex : dropIndex !== null ? dropIndex : null;

    // Trouver l'item et sa position actuelle
    const item = items.find((item) => item._id === itemId);
    if (!item) return;

    // Obtenir les items de la colonne source et trouver la position de l'item
    const itemsInSourceColumn = items.filter((i) => i.status === sourceColumnId);
    const currentPosition = itemsInSourceColumn.findIndex((i) => i._id === itemId);

    // Ne rien faire si on dépose au même endroit
    if (sourceColumnId === columnId && actualDropIndex === currentPosition) {
      return;
    }

    // Déplacer l'item
    moveItem(itemId, sourceColumnId, columnId, actualDropIndex);
  };

  const handleColumnDragOver = (e, columnId) => {
    e.preventDefault();

    // Accept dragging from both backlog items and roadmap items
    const draggedFromRoadmap = !!draggedItem;
    const draggedFromBacklog = !!draggedBacklogItem;

    if (!draggedFromRoadmap && !draggedFromBacklog) return;

    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleItemDragOver = (e, columnId, index) => {
    e.preventDefault();
    e.stopPropagation();

    // Accept dragging from both backlog items and roadmap items
    const draggedFromRoadmap = !!draggedItem;
    const draggedFromBacklog = !!draggedBacklogItem;

    if (!draggedFromRoadmap && !draggedFromBacklog) return;

    const boundingRect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const threshold = boundingRect.top + boundingRect.height / 2;

    // Determine if we're above or below the element
    const position = mouseY < threshold ? index : index + 1;

    if (dragOverItemIndex !== position || dragOverColumn !== columnId) {
      setDragOverItemIndex(position);
      setDragOverColumn(columnId);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();

    // Accept dragging from both backlog items and roadmap items
    const draggedFromRoadmap = !!draggedItem;
    const draggedFromBacklog = !!draggedBacklogItem;

    if (!draggedFromRoadmap && !draggedFromBacklog) return;

    // Get the container's bounding rectangle
    const container = e.currentTarget;
    const containerRect = container.getBoundingClientRect();

    // Horizontal scroll detection - margins for left and right edges
    const scrollMargin = 100; // px from edge to trigger scrolling
    const scrollSpeed = 200; // px per scroll interval

    // Clear any existing auto-scroll interval
    if (autoScrolling) {
      clearInterval(autoScrolling);
      setAutoScrolling(null);
    }

    // Check if we're close to the left edge
    if (e.clientX - containerRect.left < scrollMargin) {
      const intervalId = setInterval(() => {
        container.scrollLeft -= scrollSpeed;
      }, 50);
      setAutoScrolling(intervalId);
    }
    // Check if we're close to the right edge
    else if (containerRect.right - e.clientX < scrollMargin) {
      const intervalId = setInterval(() => {
        container.scrollLeft += scrollSpeed;
      }, 50);
      setAutoScrolling(intervalId);
    }
  };

  const handleDragEnd = () => {
    // Clear scrolling interval when drag ends
    if (autoScrolling) {
      clearInterval(autoScrolling);
      setAutoScrolling(null);
    }

    // Existing code
    setDraggedBacklogItem(null);
    setDragOverColumn(null);
    setDragOverItemIndex(null);
  };

  // Ajout de la fonction de suppression d'item
  const handleDeleteItem = async (item) => {
    try {
      const { ok } = await API.remove(`/roadlog_item/${item._id}`);

      if (!ok) throw new Error("Error deleting the item");

      // Mise à jour locale des items après suppression réussie
      setItems((prevItems) => prevItems.filter((i) => i._id !== item._id));
      toast.success("Item deleted successfully");
    } catch (error) {
      console.error("Error during deletion:", error);
      toast.error("Connection error");
    }
  };

  const handleRemoveFromBacklog = async (item) => {
    try {
      const { ok } = await API.put(`/roadlog_item/${item._id}`, {
        is_in_backlog: false,
      });

      if (!ok) throw new Error("Error deleting the item");

      setItems((prevItems) => prevItems.filter((i) => i._id !== item._id));
      toast.success("Item removed from backlog successfully");
    } catch (error) {
      console.error("Error during deletion:", error);
      toast.error("Connection error");
    }
  };

  const handleTicketAdded = async (newTicket, columnId) => {
    try {
      // Déterminer la position du nouveau ticket (à la fin de la colonne)
      const itemsInColumn = items.filter((item) => item.status === columnId);
      const position = itemsInColumn.length;

      // Ajouter le nouveau ticket avec la position calculée
      setItems((prevItems) => [
        ...prevItems,
        {
          ...newTicket,
          position,
          status: columnId,
        },
      ]);
      const { ok } = await API.put(`/roadlog_item/${newTicket._id}`, { position });
      if (!ok) toast.error("Failed to update position for new ticket");
    } catch (error) {
      console.error("Error during addition:", error);
      toast.error("Failed to add ticket");
    }
  };

  const handleTicketCreated = (newTicket) => {
    setIsCreateTicketModalOpen(false);
    if (newTicket.category !== "BUG") {
      setPendingTicket(newTicket);
      setIsPedagogyModalOpen(true);
      return;
    }

    query.set("item_modal_id", newTicket._id);
    setItems((prevItems) => [...prevItems, newTicket]);
    navigate({ search: query.toString() });
  };

  const handlePedagogyGoToRoadmap = () => {
    setIsPedagogyModalOpen(false);
    if (!pendingTicket) return;

    query.set("item_modal_id", pendingTicket._id);
    navigate(`/project/${project._id}/roadlog?${query.toString()}`);
  };

  const handlePedagogyAddToBacklog = async () => {
    if (!pendingTicket) return;

    try {
      const { ok } = await API.put(`/roadlog_item/${pendingTicket._id}`, {
        is_in_backlog: true,
      });

      if (!ok) throw new Error("Failed to update ticket");

      // Add to local state
      setItems((prevItems) => [...prevItems, { ...pendingTicket, is_in_backlog: true }]);

      // Open the modal
      query.set("item_modal_id", pendingTicket._id);
      navigate({ search: query.toString() });

      toast.success("Ticket ajouté au backlog");
    } catch (error) {
      console.error("Error updating ticket:", error);
      toast.error("Erreur lors de la mise à jour du ticket");
    }

    setIsPedagogyModalOpen(false);
    setPendingTicket(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter Section */}
      <div className="flex-shrink-0">
        <BacklogFilters
          filters={filters}
          setFilters={setFilters}
          project={project}
          showRoadmap={showRoadmap}
          setShowRoadmap={setShowRoadmap}
          setIsCreateTicketModalOpen={setIsCreateTicketModalOpen}
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Roadmap Extension */}
        <AnimatePresence>
          {showRoadmap && (
            <motion.div
              initial={{
                opacity: 0,
                height: 0,
                width: 0,
                borderRadius: "0.5rem",
              }}
              animate={{
                opacity: 1,
                height: "auto",
                width: "28%",
                borderRadius: "0.5rem",
              }}
              exit={{
                opacity: 0,
                height: 0,
                width: 0,
                borderRadius: "0.5rem",
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
                height: { duration: 0.4 },
                width: { duration: 0.3, delay: 0.1 },
              }}
              className="h-full mt-2 mr-1 overflow-hidden flex-shrink-0 bg-white border border-neutral-300"
              style={{
                borderTopLeftRadius: "0px",
                borderTopRightRadius: "0.5rem",
                borderBottomLeftRadius: "0.5rem",
                borderBottomRightRadius: "0.5rem",
              }}>
              {/* Roadmap Content */}
              <div className="h-full flex-1">
                <RoadmapColumn
                  project={project}
                  setDraggedItem={setDraggedItem}
                  setSelectedItem={setSelectedItem}
                  selectedItem={selectedItem}
                  filters={filters}
                  callbacks={callbacks}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`${showRoadmap ? "w-[72%]" : "w-full"} h-full overflow-hidden`}>
          <div className="flex flex-col h-full">
            <div className="flex gap-2 overflow-x-auto flex-1 h-full" onDragOver={handleDragOver}>
              {columns.map((column) => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  items={items.filter((item) => item.status === column.id)}
                  loading={loading}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  onDragOver={handleColumnDragOver}
                  onItemDragOver={handleItemDragOver}
                  onDragEnd={handleDragEnd}
                  onDeleteItem={handleDeleteItem}
                  onRemoveFromBacklog={handleRemoveFromBacklog}
                  dragOverColumn={dragOverColumn}
                  dragOverItemIndex={dragOverItemIndex}
                  draggedItem={draggedBacklogItem}
                  draggedRoadmapItem={draggedItem}
                  selectedItem={selectedItem}
                  onMoveAllItems={fetch}
                  project={project}
                  onTicketAdded={handleTicketAdded}
                />
              ))}
            </div>

            {itemModalId && (
              <ItemModal
                project={project}
                onClose={() => {
                  query.delete("item_modal_id");
                  navigate({ search: query.toString() });
                }}
                onUpdate={(item) => {
                  setItems(items.map((i) => (i._id === item._id ? item : i)));
                  if (callbacks.onItemUpdate) {
                    callbacks.onItemUpdate(item);
                  }
                }}
                onDelete={(item) => {
                  setItems(items.filter((i) => i._id !== item._id));
                  if (callbacks.onItemDelete) {
                    callbacks.onItemDelete(item);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      <CreateTicket isOpen={isCreateTicketModalOpen} onClose={() => setIsCreateTicketModalOpen(false)} project={project} onTicketCreated={handleTicketCreated} />

      <FeaturePedagogyModal
        isOpen={isPedagogyModalOpen}
        onGoToRoadmap={handlePedagogyGoToRoadmap}
        onAddToBacklog={handlePedagogyAddToBacklog}
        ticketTitle={pendingTicket?.title || ""}
      />
    </div>
  );
};

export default Backlog;
